/**
 * A aba Pendências da mesa (E205.F3.T03, ADR-0147 §5): um lugar, uma ordem, um "Resolver" por item.
 *
 * A lista é a do servidor (`prontidao.pendencias`), na ordem em que se resolve: primeiro as que
 * seguram a emissão, com a primeira em destaque (é o próximo passo); depois as informativas
 * (`bloqueia: false`: a leitura proposta, o documento de apoio a conferir, o ponto do perito), que
 * não seguram nada. Cada item diz o que falta, por quê e a peça, e "Resolver" leva aonde se resolve:
 * a aba e o bloco da mesa pelo endereço, a outra tela do caso, ou, no ponto do perito com campo, o
 * painel do dado do campo lido ("Rever leitura na fonte").
 *
 * Sem pendência, "Nada pendente".
 */
import type { MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAbrirDado } from '@/components/DocumentoDaMesa/pedidoDoDado';
import { Badge, Button, buttonVariants, EmptyState } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { caminhoDaTela, porqueDaPendencia, telaDoPasso } from '@/lib/proximoPasso';
import { bloqueia, type PendenciaDaProntidaoData } from '@/lib/resultadoAuditado';
import type { EstadoDoResultadoNaTela } from '@/lib/useResultadoDoCaso';
import { cn } from '@/lib/utils';

const t = ptBR.mesaUnica.pendencias;
const tp = ptBR.resultadoAuditado.proximoPasso;
const tf = ptBR.fichaDaLeitura;
const tc = ptBR.conferenciaDaLeitura;

/** O destino do "Resolver": a âncora da mesa (troca a aba) ou outra tela do caso. */
export function destinoDaPendencia(
  p: PendenciaDaProntidaoData,
  caseId: string,
): { ancora: string } | { caminho: string } {
  if (p.alvo) return { ancora: p.alvo.ancora ?? p.alvo.aba };
  const caminho = caminhoDaTela(telaDoPasso(p.passo), caseId);
  const daMesa = `/casos/${caseId}/mesa#`;
  return caminho.startsWith(daMesa) ? { ancora: caminho.slice(daMesa.length) } : { caminho };
}

function Resolver({
  pendencia,
  caseId,
}: {
  pendencia: PendenciaDaProntidaoData;
  caseId: string;
}): JSX.Element {
  const navigate = useNavigate();
  const { search } = useLocation();
  const abrirDado = useAbrirDado();
  const destino = destinoDaPendencia(pendencia, caseId);
  const testId = 'pendencia-resolver';
  if ('caminho' in destino) {
    return (
      <Link
        to={destino.caminho}
        className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'no-underline')}
        data-testid={testId}
      >
        {t.resolver}
      </Link>
    );
  }
  const campo = pendencia.alvo?.campo ?? null;
  const referente = pendencia.alvo?.referente_id;
  const resolver = (e: MouseEvent<HTMLButtonElement>): void => {
    navigate({ search, hash: destino.ancora });
    // O ponto do perito com campo: o painel do campo lido (o par campo e referente, E205.F3.C01),
    // com "Rever leitura na fonte".
    if (campo !== null && abrirDado !== null) {
      abrirDado({
        origem: {
          tipo: 'lido',
          chave: campo,
          linha: null,
          revisao: null,
          ...(referente !== undefined ? { referente } : {}),
        },
        impresso: '',
        secao: null,
        elemento: e.currentTarget,
      });
    }
  };
  return (
    <Button type="button" variant="secondary" size="sm" onClick={resolver} data-testid={testId}>
      {t.resolver}
    </Button>
  );
}

/**
 * E205.F3.C02 (F-E205.F3-02): o ponto do perito diz o campo e a natureza (ausência a conferir,
 * valor sem trecho, valor no papel sem uso, divergência): dois pontos do mesmo documento não saem
 * iguais.
 */
function OPontoDoPerito({
  pendencia,
}: {
  pendencia: PendenciaDaProntidaoData;
}): JSX.Element | null {
  const campo = pendencia.alvo?.campo ?? null;
  if (pendencia.motivo !== 'ponto_do_perito' || campo === null) return null;
  const rotulos: Partial<Record<string, string>> = tf.campo;
  const natureza = pendencia.alvo?.natureza ?? null;
  return (
    <p className="text-body-sm text-fg" data-testid="pendencia-ponto" data-campo={campo}>
      <span className="font-medium">{rotulos[campo] ?? campo}</span>
      {natureza !== null ? <span>: {tc.pendencia[natureza]}</span> : null}
    </p>
  );
}

function ItemDaPendencia({
  pendencia,
  caseId,
  proximo,
}: {
  pendencia: PendenciaDaProntidaoData;
  caseId: string;
  proximo: boolean;
}): JSX.Element {
  const informativa = !bloqueia(pendencia);
  return (
    <li
      className={cn(
        'flex flex-wrap items-start justify-between gap-sc-3 border px-sc-4 py-sc-3',
        proximo ? 'border-accent-200 bg-accent-50' : 'border-border',
      )}
      data-testid="pendencia-da-mesa"
      data-motivo={pendencia.motivo}
      data-proximo={proximo}
      data-bloqueia={!informativa}
    >
      <div className="min-w-0 space-y-1">
        {proximo ? <Badge variant="info">{t.proximo}</Badge> : null}
        {informativa ? <Badge variant="neutral">{t.informativa}</Badge> : null}
        <p className="text-body-md font-medium text-fg">{tp[pendencia.passo]}</p>
        <OPontoDoPerito pendencia={pendencia} />
        <p className="text-body-sm text-fg-muted">{porqueDaPendencia(pendencia)}</p>
      </div>
      <Resolver pendencia={pendencia} caseId={caseId} />
    </li>
  );
}

export interface PendenciasDaMesaProps {
  caseId: string;
  resultado: EstadoDoResultadoNaTela;
  onTentarDeNovo: () => void;
}

export function PendenciasDaMesa({
  caseId,
  resultado,
  onTentarDeNovo,
}: PendenciasDaMesaProps): JSX.Element {
  if (resultado.estado === 'carregando') {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="pendencias-lendo">
        {t.lendo}
      </p>
    );
  }
  const prontidao = resultado.estado === 'pronto' ? resultado.dados.prontidao : null;
  if (prontidao === null) {
    return (
      <div role="alert" className="space-y-sc-2" data-testid="pendencias-indisponivel">
        <p className="text-body-sm text-fg-muted">{t.indisponivel}</p>
        <Button type="button" variant="secondary" size="sm" onClick={onTentarDeNovo}>
          {t.tentarDeNovo}
        </Button>
      </div>
    );
  }
  const lista = prontidao.pendencias;
  if (lista.length === 0) {
    return (
      <EmptyState
        title={t.nadaPendente}
        description={t.nadaPendenteTexto}
        data-testid="pendencias-nada"
      />
    );
  }
  const indiceDoProximo = lista.findIndex(bloqueia);
  return (
    <section aria-label={t.rotulo} data-testid="pendencias-da-mesa">
      <ol className="space-y-sc-2">
        {lista.map((p, i) => (
          <ItemDaPendencia
            key={`${p.motivo}-${p.peca_id ?? ''}-${p.alvo?.campo ?? ''}-${i}`}
            pendencia={p}
            caseId={caseId}
            proximo={i === indiceDoProximo}
          />
        ))}
      </ol>
    </section>
  );
}

/** Quantas pendências a aba lista (o contador ao lado do nome); `null` sem leitura. */
export function quantasPendencias(resultado: EstadoDoResultadoNaTela): number | null {
  if (resultado.estado !== 'pronto' || resultado.dados.prontidao === null) return null;
  return resultado.dados.prontidao.pendencias.length;
}

/** O próximo passo que segura a emissão, para o chip do cabeçalho; `null` sem nenhum. */
export function proximoQueBloqueia(
  resultado: EstadoDoResultadoNaTela,
): PendenciaDaProntidaoData | null {
  if (resultado.estado !== 'pronto' || resultado.dados.prontidao === null) return null;
  return resultado.dados.prontidao.pendencias.find(bloqueia) ?? null;
}
